// =============================================================================
// ClinicSmsAutomation — SMS automation settings (MOD-9b sprint 1)
// =============================================================================
// Accessible at /settings/sms-automation
// Persists all fields under clinics.sms_settings JSONB via the existing
// update_clinic_settings RPC (same pattern as ClinicGeneral / ClinicBranding).
// =============================================================================

import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { useClinic, useUpdateClinicSettings } from '@/hooks/useClinicSettings';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { SettingsLoading } from './settingsChrome';
import type { Json } from '@/types/database.generated';

interface SmsAutomationSettings {
  enabled: boolean;
  sender: string;
  defaultCountryCode: string;
  dailyCap: number | undefined;
  quietHours: unknown;
  confirmations_enabled: boolean;
  reminders_enabled: boolean;
  followups_enabled: boolean;
  reminder_lead_hours: number;
  followup_offset_hours: number;
  templates: {
    confirmation: string;
    reminder: string;
    followup: string;
  };
}

function readSettings(raw: Json): SmsAutomationSettings {
  const o =
    raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, Json>) : {};
  const templates =
    o.templates && typeof o.templates === 'object' && !Array.isArray(o.templates)
      ? (o.templates as Record<string, Json>)
      : {};
  return {
    enabled: Boolean(o.enabled),
    sender: typeof o.sender === 'string' ? o.sender : 'Clinio',
    defaultCountryCode: typeof o.defaultCountryCode === 'string' ? o.defaultCountryCode : '45',
    dailyCap: typeof o.dailyCap === 'number' ? o.dailyCap : undefined,
    quietHours: o.quietHours ?? null,
    confirmations_enabled: o.confirmations_enabled !== false, // default true
    reminders_enabled: o.reminders_enabled === true, // opt-in; e-mail is the default reminder
    followups_enabled: o.followups_enabled !== false,
    reminder_lead_hours: typeof o.reminder_lead_hours === 'number' ? o.reminder_lead_hours : 24,
    followup_offset_hours:
      typeof o.followup_offset_hours === 'number' ? o.followup_offset_hours : 48,
    templates: {
      confirmation: typeof templates.confirmation === 'string' ? templates.confirmation : '',
      reminder: typeof templates.reminder === 'string' ? templates.reminder : '',
      followup: typeof templates.followup === 'string' ? templates.followup : '',
    },
  };
}

export function ClinicSmsAutomation() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { isClinicAdmin, isPlatformAdmin } = useAuth();
  const { data: clinic, isLoading, error } = useClinic(activeClinicId);
  const update = useUpdateClinicSettings(activeClinicId);

  const canEdit = Boolean(activeClinicId && (isPlatformAdmin || isClinicAdmin(activeClinicId)));

  const [confirmationsEnabled, setConfirmationsEnabled] = useState(true);
  const [remindersEnabled, setRemindersEnabled] = useState(false);
  const [followupsEnabled, setFollowupsEnabled] = useState(true);
  const [reminderLeadHours, setReminderLeadHours] = useState(24);
  const [followupOffsetHours, setFollowupOffsetHours] = useState(48);
  const [tmplConfirmation, setTmplConfirmation] = useState('');
  const [tmplReminder, setTmplReminder] = useState('');
  const [tmplFollowup, setTmplFollowup] = useState('');

  // Basic SMS settings
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [smsSender, setSmsSender] = useState('Clinio');
  const [smsCountryCode, setSmsCountryCode] = useState('45');
  const [smsDailyCap, setSmsDailyCap] = useState<number | ''>(100);
  const [quietEnabled, setQuietEnabled] = useState(false);
  const [quietStart, setQuietStart] = useState('21:00');
  const [quietEnd, setQuietEnd] = useState('08:00');

  useEffect(() => {
    if (!clinic) return;
    const s = readSettings(clinic.sms_settings);
    setConfirmationsEnabled(s.confirmations_enabled);
    setRemindersEnabled(s.reminders_enabled);
    setFollowupsEnabled(s.followups_enabled);
    setReminderLeadHours(s.reminder_lead_hours);
    setFollowupOffsetHours(s.followup_offset_hours);
    setTmplConfirmation(s.templates.confirmation);
    setTmplReminder(s.templates.reminder);
    setTmplFollowup(s.templates.followup);
    setSmsEnabled(s.enabled);
    setSmsSender(s.sender);
    setSmsCountryCode(s.defaultCountryCode);
    setSmsDailyCap(s.dailyCap ?? '');
    const qh =
      s.quietHours && typeof s.quietHours === 'object' && !Array.isArray(s.quietHours)
        ? (s.quietHours as { startMinutes?: number; endMinutes?: number })
        : null;
    if (qh && Number.isFinite(qh.startMinutes) && Number.isFinite(qh.endMinutes)) {
      setQuietEnabled(true);
      setQuietStart(minutesToHm(Number(qh.startMinutes)));
      setQuietEnd(minutesToHm(Number(qh.endMinutes)));
    } else {
      setQuietEnabled(false);
    }
  }, [clinic]);
  if (isLoading) {
    return <SettingsLoading />;
  }
  if (error) return <ErrorAlert message={t('settings.load_error')} />;
  if (!clinic) return <ErrorAlert message={t('settings.no_clinic')} />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canEdit) return;

    // Merge automation fields into the existing sms_settings JSONB
    const existing =
      clinic.sms_settings &&
      typeof clinic.sms_settings === 'object' &&
      !Array.isArray(clinic.sms_settings)
        ? (clinic.sms_settings as Record<string, Json>)
        : {};

    const payload: Record<string, Json> = {
      sms_settings: {
        ...existing,
        enabled: smsEnabled,
        sender: smsSender.trim() || 'Clinio',
        defaultCountryCode: smsCountryCode.trim() || '45',
        dailyCap: smsDailyCap !== '' ? Number(smsDailyCap) : undefined,
        quietHours: quietEnabled
          ? {
              startMinutes: hmToMinutes(quietStart),
              endMinutes: hmToMinutes(quietEnd),
              timezone: 'Europe/Copenhagen',
            }
          : null,
        confirmations_enabled: confirmationsEnabled,
        reminders_enabled: remindersEnabled,
        followups_enabled: followupsEnabled,
        reminder_lead_hours: reminderLeadHours,
        followup_offset_hours: followupOffsetHours,
        templates: {
          confirmation: tmplConfirmation.trim(),
          reminder: tmplReminder.trim(),
          followup: tmplFollowup.trim(),
        },
      } as Json,
    };

    try {
      await update.mutateAsync(payload);
      toast.success(t('sms.automation_saved'));
    } catch {
      toast.error(t('sms.automation_save_failed'));
    }
  };

  return (
    <div className="space-y-8">
      <form onSubmit={onSubmit} className="space-y-8">
        {/* ── Basic SMS settings ── */}
        <fieldset className="rounded-xl border border-ds-line bg-ds-card p-6 shadow-card">
          <legend className="px-2 text-sm font-semibold text-ds-text">
            {t('sms.settings_basic_title')}
          </legend>
          <div className="mt-4 space-y-4">
            <ToggleRow
              id="sms_enabled"
              label={t('sms.enabled')}
              hint={t('sms.enabled_hint')}
              checked={smsEnabled}
              onChange={setSmsEnabled}
              disabled={!canEdit}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="sms_sender"
                  className="block text-sm font-medium text-ds-text"
                >
                  {t('sms.sender')}
                </label>
                <p className="mt-0.5 text-xs text-ds-muted">
                  {t('sms.sender_hint')}
                </p>
                <input
                  id="sms_sender"
                  type="text"
                  maxLength={11}
                  value={smsSender}
                  onChange={(e) => setSmsSender(e.target.value)}
                  disabled={!canEdit}
                  className="mt-2 block w-full rounded-md border border-ds-field bg-ds-card px-3 py-2 text-sm text-ds-text placeholder:text-ds-placeholder focus:border-ds-teal focus:outline-none focus:shadow-ring disabled:opacity-50"
                />
              </div>
              <div>
                <label
                  htmlFor="sms_country_code"
                  className="block text-sm font-medium text-ds-text"
                >
                  {t('sms.default_country_code')}
                </label>
                <p className="mt-0.5 text-xs text-ds-muted">
                  {t('sms.default_country_code_hint')}
                </p>
                <input
                  id="sms_country_code"
                  type="text"
                  maxLength={4}
                  value={smsCountryCode}
                  onChange={(e) => setSmsCountryCode(e.target.value.replace(/\D/g, ''))}
                  disabled={!canEdit}
                  className="mt-2 block w-full rounded-md border border-ds-field bg-ds-card px-3 py-2 text-sm text-ds-text placeholder:text-ds-placeholder focus:border-ds-teal focus:outline-none focus:shadow-ring disabled:opacity-50"
                />
              </div>
            </div>
            <div>
              <label
                htmlFor="sms_daily_cap"
                className="block text-sm font-medium text-ds-text"
              >
                {t('sms.daily_cap')}
              </label>
              <p className="mt-0.5 text-xs text-ds-muted">
                {t('sms.daily_cap_hint')}
              </p>
              <input
                id="sms_daily_cap"
                type="number"
                min={1}
                max={10000}
                value={smsDailyCap}
                onChange={(e) =>
                  setSmsDailyCap(e.target.value === '' ? '' : Number(e.target.value))
                }
                disabled={!canEdit}
                placeholder={t('sms.daily_cap_placeholder') as string}
                className="mt-2 block w-full max-w-xs rounded-md border border-ds-field bg-ds-card px-3 py-2 text-sm text-ds-text placeholder:text-ds-placeholder focus:border-ds-teal focus:outline-none focus:shadow-ring disabled:opacity-50"
              />
            </div>
            <ToggleRow
              id="sms_quiet_hours"
              label={t('sms.quiet_hours')}
              hint={t('sms.quiet_hours_hint')}
              checked={quietEnabled}
              onChange={setQuietEnabled}
              disabled={!canEdit}
            />
            {quietEnabled && (
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="sms_quiet_start" className="block text-sm font-medium text-ds-text">
                    {t('sms.quiet_hours_start')}
                  </label>
                  <input
                    id="sms_quiet_start"
                    type="time"
                    value={quietStart}
                    onChange={(e) => setQuietStart(e.target.value)}
                    disabled={!canEdit}
                    className="mt-2 block w-full rounded-md border border-ds-field bg-ds-card px-3 py-2 text-sm text-ds-text focus:border-ds-teal focus:outline-none focus:shadow-ring disabled:opacity-50"
                  />
                </div>
                <div>
                  <label htmlFor="sms_quiet_end" className="block text-sm font-medium text-ds-text">
                    {t('sms.quiet_hours_end')}
                  </label>
                  <input
                    id="sms_quiet_end"
                    type="time"
                    value={quietEnd}
                    onChange={(e) => setQuietEnd(e.target.value)}
                    disabled={!canEdit}
                    className="mt-2 block w-full rounded-md border border-ds-field bg-ds-card px-3 py-2 text-sm text-ds-text focus:border-ds-teal focus:outline-none focus:shadow-ring disabled:opacity-50"
                  />
                </div>
              </div>
            )}
          </div>
        </fieldset>

        {/* ── Toggle section ── */}
        <fieldset className="rounded-xl border border-ds-line bg-ds-card p-6 shadow-card">
          <legend className="px-2 text-sm font-semibold text-ds-text">
            {t('settings.enabled_features')}
          </legend>
          <div className="mt-4 space-y-4">
            <ToggleRow
              id="confirmations_enabled"
              label={t('sms.confirmations_enabled')}
              hint={t('sms.confirmations_enabled_hint')}
              checked={confirmationsEnabled}
              onChange={setConfirmationsEnabled}
              disabled={!canEdit}
            />
            <ToggleRow
              id="reminders_enabled"
              label={t('sms.reminders_enabled')}
              hint={t('sms.reminders_enabled_hint')}
              checked={remindersEnabled}
              onChange={setRemindersEnabled}
              disabled={!canEdit}
            />
            <ToggleRow
              id="followups_enabled"
              label={t('sms.followups_enabled')}
              hint={t('sms.followups_enabled_hint')}
              checked={followupsEnabled}
              onChange={setFollowupsEnabled}
              disabled={!canEdit}
            />
          </div>
        </fieldset>

        {/* ── Timing section ── */}
        <fieldset className="rounded-xl border border-ds-line bg-ds-card p-6 shadow-card">
          <legend className="px-2 text-sm font-semibold text-ds-text">
            {t('settings.timing')}
          </legend>
          <div className="mt-4 grid gap-6 sm:grid-cols-2">
            <div>
              <label
                htmlFor="reminder_lead_hours"
                className="block text-sm font-medium text-ds-text"
              >
                {t('sms.reminder_lead_hours')}
              </label>
              <p className="mt-0.5 text-xs text-ds-muted">
                {t('sms.reminder_lead_hours_hint')}
              </p>
              <input
                id="reminder_lead_hours"
                type="number"
                min={1}
                max={168}
                value={reminderLeadHours}
                onChange={(e) => setReminderLeadHours(Number(e.target.value))}
                disabled={!canEdit}
                className="mt-2 block w-full rounded-md border border-ds-field bg-ds-card px-3 py-2 text-sm text-ds-text placeholder:text-ds-placeholder focus:border-ds-teal focus:outline-none focus:shadow-ring disabled:opacity-50"
              />
            </div>
            <div>
              <label
                htmlFor="followup_offset_hours"
                className="block text-sm font-medium text-ds-text"
              >
                {t('sms.followup_offset_hours')}
              </label>
              <p className="mt-0.5 text-xs text-ds-muted">
                {t('sms.followup_offset_hours_hint')}
              </p>
              <input
                id="followup_offset_hours"
                type="number"
                min={1}
                max={720}
                value={followupOffsetHours}
                onChange={(e) => setFollowupOffsetHours(Number(e.target.value))}
                disabled={!canEdit}
                className="mt-2 block w-full rounded-md border border-ds-field bg-ds-card px-3 py-2 text-sm text-ds-text placeholder:text-ds-placeholder focus:border-ds-teal focus:outline-none focus:shadow-ring disabled:opacity-50"
              />
            </div>
          </div>
        </fieldset>

        {/* ── Templates section ── */}
        <fieldset className="rounded-xl border border-ds-line bg-ds-card p-6 shadow-card">
          <legend className="px-2 text-sm font-semibold text-ds-text">
            {t('settings.templates')}
          </legend>
          <p className="mt-1 text-xs text-ds-muted">
            {t('sms.template_variables')}
          </p>
          <div className="mt-4 space-y-6">
            <TemplateField
              id="template_confirmation"
              label={t('sms.template_confirmation')}
              value={tmplConfirmation}
              onChange={setTmplConfirmation}
              disabled={!canEdit}
              placeholder={t('sms.template_placeholder')}
            />
            <TemplateField
              id="template_reminder"
              label={t('sms.template_reminder')}
              value={tmplReminder}
              onChange={setTmplReminder}
              disabled={!canEdit}
              placeholder={t('sms.template_placeholder')}
            />
            <TemplateField
              id="template_followup"
              label={t('sms.template_followup')}
              value={tmplFollowup}
              onChange={setTmplFollowup}
              disabled={!canEdit}
              placeholder={t('sms.template_placeholder')}
            />
          </div>
        </fieldset>

        {canEdit && (
          <div className="flex justify-end">
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function minutesToHm(minutes: number): string {
  const h = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function hmToMinutes(value: string): number {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value);
  if (!match) return 0;
  return Number(match[1]) * 60 + Number(match[2]);
}

function ToggleRow({
  id,
  label,
  hint,
  checked,
  onChange,
  disabled,
}: {
  id: string;
  label: string;
  hint: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled: boolean;
}) {
  return (
    <div className="flex items-start gap-4">
      <div className="flex h-6 items-center">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          className="h-4 w-4 rounded border-ds-field text-ds-teal focus:shadow-ring disabled:opacity-50"
        />
      </div>
      <div className="min-w-0 flex-1">
        <label htmlFor={id} className="text-sm font-medium text-ds-text">
          {label}
        </label>
        <p className="mt-0.5 text-xs text-ds-muted">{hint}</p>
      </div>
    </div>
  );
}

function TemplateField({
  id,
  label,
  value,
  onChange,
  disabled,
  placeholder,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  placeholder: string;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-ds-text">
        {label}
      </label>
      <textarea
        id={id}
        rows={3}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        className="mt-1 block w-full rounded-md border border-ds-field bg-ds-card px-3 py-2 text-sm text-ds-text placeholder:text-ds-placeholder focus:border-ds-teal focus:outline-none focus:shadow-ring disabled:opacity-50"
      />
    </div>
  );
}
